// src/utils/billingFlow.js
// ===========================================================================
// Proposal -> Deposit Invoice -> Job -> Progress / Final Invoices
//
// One place for the billing pipeline so every page that completes a job,
// approves a proposal or records a payment behaves the same way.
//
//   1. A proposal is approved with "Deposit required = Yes".
//        -> a DEPOSIT invoice is written to approvedInvoices (status Draft).
//           Its invoice number is the proposal work order, e.g. PJMC-10025.
//           No job is created yet.
//   2. That invoice is sent (Approved Invoices -> Sent section, unchanged).
//   3. Payment is recorded (Deposits page, or "Invoice Paid" on Approved
//      Invoices). Once the deposit invoice is fully paid:
//        -> an Active job is created under the next branch number,
//           e.g. PJMC-10025-2. The deposit invoice stays as Paid.
//   4. When that job (or any continuation of it) is completed, the user picks
//      Progress Payment or Final Payment and the invoice is written straight
//      to Approved Invoices. Send Invoices is no longer a required step.
//
// Every invoice that belongs to the same contract carries the same
// `contractId`. The running balance is worked out from those invoices, so it
// is always the live number:
//
//   remaining balance = job total - everything billed on the contract so far
//
// Proposal jobs use the proposal id as contractId. Ordinary jobs use their
// work order stem ("wo:5456"), so 5456, 5456-2 and 5456-3 share one ledger.
// ===========================================================================

import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { addJobTotal } from "./addJobTotal";
import { getNextContinuedWO, getWOStem } from "./continueJobUtils";
import { computeContractTotals, ledgerOwnsTotal, loadContractLedger } from "./jobContractLedger";
import { getNextFreeWorkOrder } from "./woNumbers";

/* ------------------------------------------------------------------------ */
/* Constants                                                                */
/* ------------------------------------------------------------------------ */

export const BILLING_KIND = {
  STANDARD: "standard",
  DEPOSIT: "deposit",
  PROGRESS: "progress",
  FINAL: "final",
};

export const BILLING_KIND_LABEL = {
  standard: "Standard invoice",
  deposit: "Deposit",
  progress: "Progress payment",
  final: "Final payment",
};

/* Percent choices for the deposit dropdown. 33.3333 is a true third, which
   is what "a third down" means on a contract. */
export const DEPOSIT_PERCENT_OPTIONS = [
  { value: "50", label: "50%" },
  { value: "33.3333", label: "33⅓% (one third)" },
  { value: "25", label: "25%" },
  { value: "40", label: "40%" },
  { value: "30", label: "30%" },
  { value: "20", label: "20%" },
  { value: "15", label: "15%" },
  { value: "10", label: "10%" },
  { value: "75", label: "75%" },
];

/* Empty values for the proposal form, spread into each page's state. */
export const EMPTY_PROPOSAL_BILLING = {
  depositRequired: "No",
  depositMode: "percent",
  depositPercent: "50",
  depositManualAmount: "",
  depositAmount: 0,
};

const SETTINGS_DOC = ["appSettings", "billingFlow"];

/* ------------------------------------------------------------------------ */
/* Small helpers                                                            */
/* ------------------------------------------------------------------------ */

export const parseMoney = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
};

export const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

export const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const dateKey = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value?.seconds) {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }
  if (typeof value === "string") return value.slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const cleanMarkers = (text = "") =>
  String(text)
    .replace(/\(\(b\)\)/gi, "")
    .replace(/\(\(i\)\)/gi, "")
    .replace(/\(\(r\)\)/gi, "")
    .replace(/\(\(-\)\)/g, "")
    .replace(/\s+/g, " ")
    .trim();

const stripUndefined = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined) delete obj[key];
  });
  return obj;
};

/* ------------------------------------------------------------------------ */
/* Deposit math (proposal form)                                             */
/* ------------------------------------------------------------------------ */

/**
 * The deposit for a proposal.
 * mode "percent" -> percent of the estimated total.
 * mode "manual"  -> the typed amount, capped at the total when there is one.
 */
export const computeDepositAmount = ({ total, mode, percent, manualAmount }) => {
  const jobTotal = parseMoney(total);

  if (mode === "manual") {
    const manual = round2(parseMoney(manualAmount));
    return jobTotal > 0 ? Math.min(manual, jobTotal) : manual;
  }

  const pct = parseMoney(percent);
  if (!jobTotal || !pct) return 0;
  return round2((jobTotal * pct) / 100);
};

/** Copy the billing fields off a stored proposal into form state. */
export const pickProposalBilling = (proposal = {}) => ({
  depositRequired: proposal.depositRequired === "Yes" ? "Yes" : "No",
  depositMode: proposal.depositMode === "manual" ? "manual" : "percent",
  depositPercent:
    proposal.depositPercent !== undefined && proposal.depositPercent !== ""
      ? String(proposal.depositPercent)
      : EMPTY_PROPOSAL_BILLING.depositPercent,
  depositManualAmount:
    proposal.depositManualAmount !== undefined ? String(proposal.depositManualAmount) : "",
  depositAmount: parseMoney(proposal.depositAmount),
});

export const getProposalContractTotal = (proposal = {}) =>
  round2(
    parseMoney(proposal.proposalTotal) ||
      parseMoney(proposal.contractAmount) ||
      parseMoney(proposal.estimatedTotal)
  );

/** The deposit a proposal asks for, recomputed so a stale saved number never wins. */
export const getProposalDeposit = (proposal = {}) => {
  if (proposal.depositRequired !== "Yes") return 0;

  const recomputed = computeDepositAmount({
    total: getProposalContractTotal(proposal),
    mode: proposal.depositMode,
    percent: proposal.depositPercent,
    manualAmount: proposal.depositManualAmount,
  });

  return recomputed || round2(parseMoney(proposal.depositAmount));
};

/* ------------------------------------------------------------------------ */
/* Invoice text                                                             */
/* ------------------------------------------------------------------------ */

/**
 * The lines at the top of every deposit / progress / final invoice.
 *
 *   Invoice - Roof Repair - Deposit
 *   Job Total: $10,000.00
 *   Deposit due now: $5,000.00
 *   Remaining balance: $5,000.00
 *
 * It is written into the invoice description, so the on-screen invoice, the
 * printed copy, the emailed body and the emailed PDF all show it without any
 * change to the Cloud Function.
 */
export const buildBillingHeader = ({
  scope,
  kind,
  sequence,
  contractTotal,
  previouslyBilled,
  amount,
}) => {
  const scopeText = cleanMarkers(scope) || "Work Order";
  const total = round2(contractTotal);
  const before = round2(previouslyBilled);
  const due = round2(amount);
  const remaining = round2(total - before - due);

  let title = "";
  let dueLabel = "";

  if (kind === BILLING_KIND.DEPOSIT) {
    title = `Invoice - ${scopeText} - Deposit`;
    dueLabel = "Deposit due now";
  } else if (kind === BILLING_KIND.PROGRESS) {
    title = `Invoice - ${scopeText} - Progress Payment ${sequence || 1}`;
    dueLabel = "Progress payment due now";
  } else if (kind === BILLING_KIND.FINAL) {
    title = `Invoice - ${scopeText} - Final Payment`;
    dueLabel = "Final payment due now";
  } else {
    return "";
  }

  const lines = [title, `Job Total: ${formatMoney(total)}`];
  if (kind !== BILLING_KIND.DEPOSIT && before > 0) {
    lines.push(`Previously billed: ${formatMoney(before)}`);
  }
  lines.push(`${dueLabel}: ${formatMoney(due)}`);
  lines.push(`Remaining balance: ${formatMoney(remaining)}`);

  return lines.join("\n");
};

/* The description the Send Invoices "Approved Invoice" button always wrote. */
const buildStandardDescription = (job = {}) =>
  `
${job.scope || ""}

${job.jobDescription || ""}
  `.trim();

/* ------------------------------------------------------------------------ */
/* Contract ledger                                                          */
/* ------------------------------------------------------------------------ */

/** Which ledger a job's invoices belong to. */
export const getContractIdForJob = (job = {}) => {
  if (job?.contractBilling?.contractId) return job.contractBilling.contractId;
  const stem = getWOStem(job?.wo);
  if (stem) return `wo:${stem}`;
  return job?.id ? `job:${job.id}` : "";
};

/** True for jobs that came out of the deposit flow. */
export const isDepositContractJob = (job = {}) =>
  job?.contractBilling?.source === "proposal-deposit";

export const loadContractInvoices = async (contractId) => {
  if (!contractId) return [];
  const snap = await getDocs(
    query(collection(db, "approvedInvoices"), where("contractId", "==", contractId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/* Standard invoices bill their own work order in full and are not part of a
   running contract balance. Only deposit / progress / final are. */
export const isLedgerInvoice = (invoice) =>
  [BILLING_KIND.DEPOSIT, BILLING_KIND.PROGRESS, BILLING_KIND.FINAL].includes(invoice?.billingKind);

const createdMillis = (invoice) => {
  const c = invoice?.createdAt;
  if (c?.seconds) return c.seconds * 1000;
  const t = new Date(c || invoice?.invoiceDate || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

/**
 * Where a contract stands. `excludeInvoiceId` leaves one invoice out, which
 * is how an open invoice works out what was billed BEFORE it.
 */
export const summarizeLedger = (invoices = [], contractTotal = 0, excludeInvoiceId = null) => {
  const rows = invoices
    .filter((inv) => inv && inv.id !== excludeInvoiceId)
    .filter(isLedgerInvoice)
    .sort((a, b) => {
      const sa = Number(a.billingOrder ?? 0);
      const sb = Number(b.billingOrder ?? 0);
      if (sa !== sb) return sa - sb;
      return createdMillis(a) - createdMillis(b);
    });

  const billed = round2(rows.reduce((sum, inv) => sum + parseMoney(inv.amount ?? inv.rate), 0));
  const progressCount = rows.filter((inv) => inv.billingKind === BILLING_KIND.PROGRESS).length;
  const total =
    round2(parseMoney(contractTotal)) ||
    round2(
      Math.max(0, ...rows.map((inv) => parseMoney(inv.contractTotal)))
    );

  return {
    rows,
    contractTotal: total,
    billed,
    remaining: round2(total - billed),
    progressCount,
    hasDeposit: rows.some((inv) => inv.billingKind === BILLING_KIND.DEPOSIT),
    hasFinal: rows.some((inv) => inv.billingKind === BILLING_KIND.FINAL),
    depositAmount: round2(
      rows
        .filter((inv) => inv.billingKind === BILLING_KIND.DEPOSIT)
        .reduce((sum, inv) => sum + parseMoney(inv.amount), 0)
    ),
  };
};

/**
 * The job total used for a job's billing: the contract total when the job
 * carries one, otherwise the job's own total - totalAmount, then
 * estimatedTotal (the order the old Approved Invoice button used), then the
 * job_totals record.
 */
export const getJobTotalForBilling = async (job = {}) => {
  // Job Money ledger (change orders / adjustments) wins when it exists: it is
  // the original total plus every change since.
  // A ledger that only lists linked WOs does not own the total.
  const ledger = await loadContractLedger(getContractIdForJob(job));
  if (ledger && ledgerOwnsTotal(ledger)) {
    return round2(computeContractTotals(ledger).current);
  }

  const fromContract = parseMoney(job?.contractBilling?.contractTotal);
  if (fromContract) return round2(fromContract);

  const fromJob =
    parseMoney(job?.totalAmount) ||
    parseMoney(job?.estimatedTotal) ||
    parseMoney(job?.contractAmount);
  if (fromJob) return round2(fromJob);

  const wo = job?.wo ? String(job.wo) : "";
  if (wo) {
    try {
      const snap = await getDoc(doc(db, "job_totals", wo));
      const total = parseMoney(snap.exists() ? snap.data()?.total : 0);
      if (total) return round2(total);
    } catch (err) {
      console.warn("job_totals lookup failed:", err);
    }
  }

  return 0;
};

/* ------------------------------------------------------------------------ */
/* Work order numbers                                                       */
/* ------------------------------------------------------------------------ */

/* Same counter the proposal pages use, skipping numbers already on a record
   (utils/woNumbers.js), so numbers never collide. */
const getNextProposalWorkOrder = (prefix) => getNextFreeWorkOrder(prefix);

/* A WO the proposal already carries is only reused when no OTHER property
   has it. Market Square already on PJMC-10045 means this proposal gets a
   fresh number instead of sharing it. */
const proposalWOIsSafe = async (proposal, wo) => {
  const site = normSite(proposal.siteName);
  const spellings = Array.from(new Set([wo, wo.toUpperCase(), wo.toLowerCase()]));
  for (const col of ["jobs", "addItem", "approvedInvoices", "proposals"]) {
    const snap = await getDocs(query(collection(db, col), where("wo", "in", spellings)));
    const clash = snap.docs.some((d) => {
      if (col === "proposals" && d.id === proposal.id) return false;
      const data = d.data() || {};
      if (data.proposalId && data.proposalId === proposal.id) return false;
      return normSite(data.siteName || data.propertyName) !== site;
    });
    if (clash) return false;
  }
  return true;
};

const companyKeyForPrefix = (prefix = "") => (String(prefix).toUpperCase().includes("DCS") ? "DCS" : "JMC");

/**
 * A deposit invoice that came from an approved proposal. Only these create a
 * job when paid. A "Deposit" picked in the popup for an ordinary job already
 * has its job, so paying it must not make another one.
 */
export const isProposalDepositInvoice = (invoice = {}) =>
  invoice?.billingKind === BILLING_KIND.DEPOSIT &&
  (invoice.createdFrom === "proposal-approval" || (invoice.proposalId && !invoice.sourceJobId));

const CLAIM_STALE_MS = 5 * 60 * 1000;

/* ------------------------------------------------------------------------ */
/* 1. Proposal approved -> deposit invoice                                  */
/* ------------------------------------------------------------------------ */

const findDepositInvoice = async (contractId) => {
  const invoices = await loadContractInvoices(contractId);
  return invoices.find((inv) => inv.billingKind === BILLING_KIND.DEPOSIT) || null;
};

/**
 * Called first thing inside every proposal page's upsertJobFromProposal.
 *
 * Returns true when the deposit flow owns this proposal (a deposit invoice was
 * created, or already exists). The page must then STOP and not create a job -
 * the job is created later, when the deposit is paid.
 *
 * Returns false for proposals without a deposit, so the page carries on with
 * exactly what it always did.
 */
export const routeApprovedProposalToDeposit = async (proposal, prefix = "PJMC") => {
  if (!proposal?.id) return false;

  let fresh = proposal;
  let deposit = 0;

  // Reading only. Any failure here falls back to the page's original
  // behavior, so a hiccup never blocks a proposal that has no deposit.
  try {
    const snap = await getDoc(doc(db, "proposals", proposal.id));
    fresh = snap.exists() ? { id: snap.id, ...snap.data() } : proposal;

    // Already in the deposit flow (approved twice, or toggled back and forth).
    const existing = await findDepositInvoice(fresh.id);
    if (existing) return true;

    deposit = getProposalDeposit(fresh);
    if (fresh.depositRequired !== "Yes" || deposit <= 0) return false;

    // Approved before the deposit flow existed and already has its job: keep
    // the original behavior (reactivate that job) instead of billing a deposit.
    const jobSnap = await getDocs(
      query(collection(db, "jobs"), where("proposalId", "==", fresh.id))
    );
    if (!jobSnap.empty) return false;
  } catch (err) {
    console.error("Deposit check failed; using the original approval behavior:", err);
    return false;
  }

  try {
    await createDepositInvoiceForProposal(fresh, prefix);
    alert(
      "Proposal approved.\n\nA deposit invoice was created in Approved Invoices. " +
        "The job will be created automatically once the deposit is paid."
    );
    return true;
  } catch (err) {
    console.error("Deposit routing failed:", err);
    alert(
      "The proposal was approved, but the deposit invoice could not be created.\n\n" +
        (err?.message || err)
    );
    // Returning true stops the page from creating a job behind a failed
    // deposit invoice. Toggling approval again retries.
    return true;
  }
};

export const createDepositInvoiceForProposal = async (proposal, prefix = "PJMC") => {
  const contractTotal = getProposalContractTotal(proposal);
  const deposit = getProposalDeposit(proposal);

  // One deposit invoice per proposal, enforced by a fixed document id. Two
  // clicks (or two tabs) end up on the same document instead of two invoices.
  const invoiceRef = doc(db, "approvedInvoices", `deposit_${proposal.id}`);
  const already = await getDoc(invoiceRef);
  if (already.exists()) return { id: already.id, ...already.data(), existed: true };

  let wo = String(proposal.wo || "").trim();
  if (wo && !(await proposalWOIsSafe(proposal, wo).catch(() => true))) {
    console.warn(`Proposal WO ${wo} is already used at another property - giving this proposal a new number.`);
    wo = "";
  }
  if (!wo) wo = await getNextProposalWorkOrder(prefix);

  const header = buildBillingHeader({
    scope: proposal.scope,
    kind: BILLING_KIND.DEPOSIT,
    sequence: 0,
    contractTotal,
    previouslyBilled: 0,
    amount: deposit,
  });

  const percentOfTotal = contractTotal ? round2((deposit / contractTotal) * 100) : 0;

  const invoiceData = stripUndefined({
    sourceJobId: "",
    jobId: "",
    proposalId: proposal.id,
    contractId: proposal.id,
    wo,
    siteName: proposal.siteName || "",
    propertyName: proposal.siteName || "",
    companyName: proposal.companyName || "",
    company: companyKeyForPrefix(prefix),
    management: proposal.management || "",
    managementCompany: proposal.management || "",
    managerName: proposal.managerName || "",
    address: proposal.address || "",
    cityStateZip: proposal.cityStateZip || "",
    scope: proposal.scope || "",

    invoiceDate: todayKey(),
    serviceDate: "",
    terms: "Due on receipt",
    productService: "Contract",
    customProductService: "",

    description: header,
    billingHeader: header,

    qty: 1,
    rate: deposit,
    amount: deposit,
    status: "Draft",

    billingKind: BILLING_KIND.DEPOSIT,
    billingOrder: 0,
    billingSequence: 0,
    contractTotal,
    previouslyBilled: 0,
    remainingBalance: round2(contractTotal - deposit),
    depositPercent: percentOfTotal,
    depositMode: proposal.depositMode || "percent",
    proposalPrefix: prefix,

    createdFrom: "proposal-approval",
    createdAt: serverTimestamp(),
  });

  const created = await runTransaction(db, async (tx) => {
    const snap = await tx.get(invoiceRef);
    if (snap.exists()) return false;
    tx.set(invoiceRef, invoiceData);
    return true;
  });
  const ref = invoiceRef;
  if (!created) return { id: ref.id, existed: true };

  await updateDoc(doc(db, "proposals", proposal.id), {
    wo,
    contractTotal,
    depositAmount: deposit,
    depositInvoiceId: ref.id,
    depositInvoiceWO: wo,
    billingStage: "Deposit invoice created",
    updatedAt: serverTimestamp(),
  });

  return { id: ref.id, ...invoiceData };
};

/* ------------------------------------------------------------------------ */
/* 2. Deposit paid -> Active job                                            */
/* ------------------------------------------------------------------------ */

/**
 * Creates the Active job for a paid deposit invoice. Safe to call more than
 * once: the invoice is claimed in a transaction, so two tabs (or the Deposits
 * page and Approved Invoices) cannot both create a job.
 *
 * Returns { id, wo } of the job, or null when nothing was done.
 */
export const createJobFromPaidDeposit = async (invoiceOrId) => {
  const invoiceId = typeof invoiceOrId === "string" ? invoiceOrId : invoiceOrId?.id;
  if (!invoiceId) return null;

  const invoiceRef = doc(db, "approvedInvoices", invoiceId);

  // Claim it. Whoever sets contractJobClaimedAt first creates the job.
  let invoice = null;
  const claimed = await runTransaction(db, async (tx) => {
    const snap = await tx.get(invoiceRef);
    if (!snap.exists()) return false;
    invoice = { id: snap.id, ...snap.data() };

    if (!isProposalDepositInvoice(invoice)) return false;
    if (invoice.contractJobId) return false;

    // A claim left behind by a tab that closed mid-way goes stale after a
    // few minutes, so the job can still be created.
    const claimedAt = invoice.contractJobClaimedAt ? new Date(invoice.contractJobClaimedAt).getTime() : 0;
    if (claimedAt && Date.now() - claimedAt < CLAIM_STALE_MS) return false;

    tx.update(invoiceRef, { contractJobClaimedAt: new Date().toISOString() });
    return true;
  });

  if (!claimed || !invoice) return null;

  try {
    // Belt and braces: a job may already point back at this invoice.
    const already = await getDocs(
      query(collection(db, "jobs"), where("createdFromDepositInvoiceId", "==", invoice.id))
    );
    if (!already.empty) {
      const existing = already.docs[0];
      await updateDoc(invoiceRef, {
        contractJobId: existing.id,
        contractJobWO: existing.data()?.wo || "",
      });
      return { id: existing.id, wo: existing.data()?.wo || "", existed: true };
    }

    let proposal = {};
    if (invoice.proposalId) {
      const pSnap = await getDoc(doc(db, "proposals", invoice.proposalId));
      if (pSnap.exists()) proposal = { id: pSnap.id, ...pSnap.data() };
    }

    const contractTotal =
      round2(parseMoney(invoice.contractTotal)) || getProposalContractTotal(proposal);
    const depositAmount = round2(parseMoney(invoice.amount));
    const wo = await getNextContinuedWO(invoice.wo);

    const images =
      proposal.imageURLs && proposal.imageURLs.length
        ? proposal.imageURLs
        : proposal.images || [];

    const jobData = stripUndefined({
      proposalId: invoice.proposalId || "",
      companyName: proposal.companyName || invoice.companyName || "",
      companyAddress: proposal.companyAddress || "",
      companyCityStateZip: proposal.companyCityStateZip || "",
      siteName: proposal.siteName || invoice.siteName || "",
      management: proposal.management || invoice.management || "",
      managerName: proposal.managerName || invoice.managerName || "",
      address: proposal.address || invoice.address || "",
      cityStateZip: proposal.cityStateZip || invoice.cityStateZip || "",
      scope: proposal.scope || invoice.scope || "",
      jobDescription: proposal.jobDescription || "",
      price: proposal.price || "",
      proposalTotal: contractTotal,
      estimatedTotal: contractTotal,
      contractAmount: contractTotal,
      submittedDate: proposal.submittedDate || "",
      submittedBy: proposal.submittedBy || "",
      terms: proposal.terms || "",
      warrantyName: proposal.warrantyName || "",
      warranty: proposal.warranty || "",
      images,
      wo,
      status: "Active",
      createdAt: new Date().toISOString(),

      createdFromDepositInvoiceId: invoice.id,
      contractBilling: {
        source: "proposal-deposit",
        contractId: invoice.contractId || invoice.proposalId || invoice.id,
        proposalId: invoice.proposalId || "",
        contractTotal,
        depositAmount,
        depositInvoiceId: invoice.id,
        depositWO: invoice.wo || "",
      },
    });

    const jobRef = await addDoc(collection(db, "jobs"), jobData);

    if (wo && contractTotal > 0) {
      await addJobTotal({
        jobId: String(wo),
        wo: String(wo),
        proposalId: invoice.proposalId || null,
        total: contractTotal,
        source: "deposit-paid",
      });
    }

    await updateDoc(invoiceRef, {
      contractJobId: jobRef.id,
      contractJobWO: wo,
      updatedAt: new Date().toISOString(),
    });

    if (invoice.proposalId) {
      try {
        await updateDoc(doc(db, "proposals", invoice.proposalId), {
          billingStage: "Deposit paid - job active",
          depositPaidAt: new Date().toISOString(),
          contractJobId: jobRef.id,
          contractJobWO: wo,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.warn("Could not update proposal after deposit paid:", err);
      }
    }

    return { id: jobRef.id, wo };
  } catch (err) {
    // Release the claim so the next attempt can try again.
    try {
      await updateDoc(invoiceRef, { contractJobClaimedAt: null });
    } catch (releaseErr) {
      console.warn("Could not release deposit claim:", releaseErr);
    }
    throw err;
  }
};

/* Money recorded on the Deposits page against one invoice number. Payments
   marked "invoice only" are a paper trail for a split check, not money, so
   they are left out - the same rule Job Profit uses. */
const sumPaymentsForWO = async (wo) => {
  const key = String(wo || "").trim();
  if (!key) return 0;
  const snap = await getDocs(query(collection(db, "deposits"), where("invoiceNumber", "==", key)));
  return round2(
    snap.docs.reduce((sum, d) => {
      const p = d.data() || {};
      if (p.invoiceOnly || p.splitInvoiceOnly) return sum;
      return sum + parseMoney(p.amount);
    }, 0)
  );
};

/**
 * Has the customer paid anything on this deposit invoice yet?
 *
 * The job is created on the FIRST payment, not only once the deposit is paid
 * to the penny. Before, a deposit recorded as $3,333 against a $3,333.33
 * invoice, or paid in two checks, left the proposal with no job at all and
 * nothing on screen said why.
 */
export const depositHasPayment = (invoice = {}, paidOnWO = 0) =>
  invoice.status === "Paid" ||
  round2(parseMoney(invoice.paidAmount)) > 0 ||
  round2(paidOnWO) > 0;

/**
 * For the Deposits page: after a payment is saved against a work order, if
 * that work order is a proposal deposit invoice:
 *   - the invoice records what has been paid (Paid once it is paid in full)
 *   - the Active job is created on the first payment
 *
 * Returns { id, wo, existed, paid, invoiceAmount, paidInFull } for the job,
 * or null when the WO is not a deposit invoice that still needs its job.
 */
const normSite = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const handleDepositPaymentForWO = async (wo, { property = "" } = {}) => {
  const key = String(wo || "").trim();
  if (!key) return null;

  const invSnap = await getDocs(
    query(collection(db, "approvedInvoices"), where("wo", "==", key))
  );
  let depositInvoices = invSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((inv) => isProposalDepositInvoice(inv) && !inv.contractJobId);

  // Same WO at another property (a reused number): the payment belongs to
  // the deposit at the property it was recorded for.
  const here = property
    ? depositInvoices.filter((inv) => normSite(inv.siteName || inv.propertyName) === normSite(property))
    : [];
  if (here.length) depositInvoices = here;

  if (!depositInvoices.length) return null;

  const paid = await sumPaymentsForWO(key);

  let created = null;
  for (const invoice of depositInvoices) {
    if (!depositHasPayment(invoice, paid)) continue;

    const amount = round2(parseMoney(invoice.amount ?? invoice.rate));
    const paidInFull = invoice.status === "Paid" || (amount > 0 && paid >= amount);

    // Keep the invoice's own record of what came in. Status only moves to
    // Paid when the deposit is covered; a part payment leaves it where it was.
    const patch = {
      paidAmount: paid,
      paymentMatchedFrom: "deposits",
      updatedAt: new Date().toISOString(),
    };
    if (paidInFull && invoice.status !== "Paid") {
      patch.status = "Paid";
      patch.paidAt = new Date().toISOString();
    }
    if (invoice.status !== "Paid") {
      await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);
    }

    const result = await createJobFromPaidDeposit(invoice.id);
    if (result) created = { ...result, paid, invoiceAmount: amount, paidInFull };
  }

  return created;
};

/**
 * Self-healing sweep: every proposal deposit invoice that has money against
 * it but no job yet gets its job now. Covers payments that never went through
 * the Deposits page form - Stripe pay links (recorded by the Cloud Function),
 * bulk uploads, Job Estimate Spreadsheet and Bank Statement entries - and any
 * earlier attempt that failed half way.
 *
 * One single-field query, so no index is needed. Safe to run from several
 * pages at once: createJobFromPaidDeposit claims each invoice in a
 * transaction, so a job is never created twice.
 *
 * Returns [{ id, wo, depositWO }] for the jobs created by this call.
 */
export const ensureJobsForDepositPayments = async () => {
  const snap = await getDocs(
    query(collection(db, "approvedInvoices"), where("billingKind", "==", BILLING_KIND.DEPOSIT))
  );
  const waiting = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((inv) => isProposalDepositInvoice(inv) && !inv.contractJobId);

  const created = [];
  for (const invoice of waiting) {
    try {
      const paid = await sumPaymentsForWO(invoice.wo);
      if (!depositHasPayment(invoice, paid)) continue;
      const result = await createJobFromPaidDeposit(invoice.id);
      if (result?.id && !result.existed) created.push({ ...result, depositWO: invoice.wo || "" });
    } catch (err) {
      console.error(`Could not create the job for deposit ${invoice.wo || invoice.id}:`, err);
    }
  }
  return created;
};

/* ------------------------------------------------------------------------ */
/* 3. Job completed -> invoice straight into Approved Invoices              */
/* ------------------------------------------------------------------------ */

/**
 * Done, no Approved Invoice yet - ignoring "Bill Later". Used by the
 * Approved Invoices page to catch a Bill Later job whose next WO was deleted.
 */
export const isUninvoicedCompletedJob = (job = {}) => {
  if (!job || job.__collection === "addItem") return false;
  if (job.status !== "Completed") return false;
  if (job.approvedInvoice) return false;
  // A finished project DAY is not the end of the project. Only the day that
  // closes the project ("Project Completed") is billed.
  if (job.isProject && job.projectStatus && job.projectStatus !== "Project Completed") {
    return false;
  }
  return true;
};

/** Jobs that are done but have no Approved Invoice yet. */
export const isAwaitingInvoice = (job = {}) => {
  if (!isUninvoicedCompletedJob(job)) return false;
  // "Bill Later": the job continues on the next WO and is billed there.
  if (job.billLater) return false;
  return true;
};

/** The day a job was completed, for deciding old backlog vs new work. */
export const getCompletionDateKey = (job = {}) => {
  const keys = [
    dateKey(job.projectCompletedAt),
    ...(Array.isArray(job.completedDates) ? job.completedDates.map(dateKey) : []),
  ].filter(Boolean);
  return keys.sort().pop() || "";
};

/**
 * Everything the billing popup needs to show for one job: job total, what was
 * billed before, the suggested amount for each kind.
 */
export const getBillingContextForJob = async (job = {}) => {
  const contractId = getContractIdForJob(job);
  const [jobTotal, invoices, moneyLedger] = await Promise.all([
    getJobTotalForBilling(job),
    loadContractInvoices(contractId),
    loadContractLedger(contractId),
  ]);

  // An invoice for THIS job (shouldn't exist when the popup opens, but if it
  // does it must not be counted as "previously billed" against itself).
  const ledger = summarizeLedger(
    invoices.filter((inv) => inv.sourceJobId !== job.id),
    jobTotal
  );

  // Everything already invoiced on this WO chain by OTHER jobs, any kind
  // (Standard included; $0 "no payment" left out). Shown as a warning in the
  // popup so a continued job is never billed its full total a second time.
  const chainRows = invoices.filter(
    (inv) => inv.sourceJobId !== job.id && !inv.noPayment && round2(parseMoney(inv.amount ?? inv.rate)) > 0
  );

  return {
    contractId,
    chainInvoices: chainRows,
    chainBilled: round2(chainRows.reduce((s, inv) => s + parseMoney(inv.amount ?? inv.rate), 0)),
    jobTotal: ledger.contractTotal || jobTotal,
    previouslyBilled: ledger.billed,
    remaining: round2((ledger.contractTotal || jobTotal) - ledger.billed),
    progressCount: ledger.progressCount,
    hasDeposit: ledger.hasDeposit,
    hasFinal: ledger.hasFinal,
    depositAmount: ledger.depositAmount,
    isContractJob: isDepositContractJob(job) || ledger.rows.length > 0,
    invoices: ledger.rows,
    // Set when Job Money has change orders / adjustments: the total comes
    // from there and is not edited in the billing popup.
    totalFromJobMoney: !!moneyLedger && ledgerOwnsTotal(moneyLedger),
    changeOrdersTotal: moneyLedger ? computeContractTotals(moneyLedger).changeOrders : 0,
  };
};

/**
 * Writes the Approved Invoice for a completed job.
 *
 *   kind     standard | deposit | progress | final
 *   amount   what this invoice bills (standard defaults to the job total,
 *            final defaults to the remaining balance)
 *   jobTotal the contract / job total; saved onto the job so continuations
 *            of the job carry it forward
 *
 * Returns { id, existed } for the invoice.
 */
export const createInvoiceForCompletedJob = async (job, options = {}) => {
  if (!job?.id) throw new Error("No job to invoice.");

  const kind = options.kind || BILLING_KIND.STANDARD;
  const origin = options.origin || "completion";
  // Send Invoices also lists completed rows from the addItem collection.
  const jobCollection = job.__collection === "addItem" ? "addItem" : "jobs";

  // One invoice per job, same rule the old Approved Invoice button used.
  const existingSnap = await getDocs(
    query(collection(db, "approvedInvoices"), where("sourceJobId", "==", job.id))
  );
  if (!existingSnap.empty) {
    await updateDoc(doc(db, jobCollection, job.id), {
      approvedInvoice: true,
      approvedInvoiceId: existingSnap.docs[0].id,
    });
    return { id: existingSnap.docs[0].id, existed: true };
  }

  const context = await getBillingContextForJob(job);
  const jobTotal = round2(parseMoney(options.jobTotal) || context.jobTotal);
  const previouslyBilled = kind === BILLING_KIND.STANDARD ? 0 : context.previouslyBilled;

  // An amount the user typed (including 0) is respected. Only when no amount
  // was passed at all (automatic sweep) is it worked out from the job total.
  const amountGiven =
    options.amount !== undefined && options.amount !== null && options.amount !== "";
  let amount = round2(parseMoney(options.amount));
  if (!amountGiven) {
    if (kind === BILLING_KIND.FINAL) amount = round2(jobTotal - previouslyBilled);
    else if (kind === BILLING_KIND.STANDARD) amount = jobTotal;
  }
  if (amount < 0) amount = 0;

  const sequence =
    kind === BILLING_KIND.PROGRESS ? context.progressCount + 1 : 0;

  const header = buildBillingHeader({
    scope: job.scope,
    kind,
    sequence,
    contractTotal: jobTotal,
    previouslyBilled,
    amount,
  });

  const ledgerKind = kind !== BILLING_KIND.STANDARD;
  const description = ledgerKind ? header : buildStandardDescription(job);

  const invoiceData = stripUndefined({
    sourceJobId: job.id || "",
    jobId: job.id || "",
    wo: job.wo || "",
    siteName: job.siteName || "",
    propertyName: job.propertyName || "",
    companyName: job.companyName || "",
    management: job.management || "",
    managementCompany: job.management || job.managementCompany || "",
    address: job.address || "",
    city: job.city || "",
    state: job.state || "",
    zip: job.zip || "",
    cityStateZip: job.cityStateZip || "",
    scope: job.scope || "",
    proposalId: job.proposalId || "",

    invoiceDate: todayKey(),
    serviceDate: getCompletionDateKey(job) || dateKey(job.startDate) || "",
    terms: "Net 30",
    productService: "Work Order",
    customProductService: "",

    description,
    billingHeader: header || "",

    qty: 1,
    rate: amount,
    amount,
    status: "Draft",

    billingKind: kind,
    billingOrder: ledgerKind ? Date.now() : 0,
    billingSequence: sequence,
    contractId: context.contractId,
    contractTotal: jobTotal,
    previouslyBilled,
    remainingBalance: ledgerKind ? round2(jobTotal - previouslyBilled - amount) : 0,

    createdFrom: origin,
    createdAt: serverTimestamp(),
  });

  // One invoice per job, enforced by a fixed document id so a double load
  // (two tabs, or React StrictMode in development) cannot write two.
  const ref = doc(db, "approvedInvoices", `job_${job.id}`);
  const wrote = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return false;
    tx.set(ref, invoiceData);
    return true;
  });
  if (!wrote) {
    await updateDoc(doc(db, jobCollection, job.id), { approvedInvoice: true, approvedInvoiceId: ref.id });
    return { id: ref.id, existed: true };
  }

  const jobPatch = {
    approvedInvoice: true,
    approvedInvoiceId: ref.id,
    approvedInvoiceAt: new Date().toISOString(),
    billingKind: kind,
    updatedAt: serverTimestamp(),
  };

  // Deposit / progress / final put the job on a running contract. Saving it
  // on the job means a Continue copies it, so the next WO keeps the same job
  // total and the same ledger. A standard invoice leaves the job alone.
  if (ledgerKind) {
    jobPatch.contractBilling = {
      ...(job.contractBilling || {}),
      contractId: context.contractId,
      contractTotal: jobTotal,
    };
  }

  await updateDoc(doc(db, jobCollection, job.id), jobPatch);

  // A Continue made before this invoice (Home's Continue completes the job
  // and copies it in one step) produced a job without the contract on it.
  // Hand it over so that job bills against the same total and balance.
  if (ledgerKind && jobCollection === "jobs") {
    try {
      const kids = await getDocs(
        query(collection(db, "jobs"), where("continuedFromJobId", "==", job.id))
      );
      await Promise.all(
        kids.docs
          .filter((d) => !d.data()?.contractBilling?.contractId)
          .map((d) => updateDoc(d.ref, { contractBilling: jobPatch.contractBilling }))
      );
    } catch (err) {
      console.warn("Could not pass the contract on to the continued job:", err);
    }
  }

  return { id: ref.id, existed: false, invoice: { id: ref.id, ...invoiceData } };
};

/**
 * "No Payment Needed" in the popup: warranty, inspection, proposal / estimate
 * visits, call-backs - work we don't get paid for.
 * Writes ONE $0.00 Approved Invoice already marked Paid (so it sits in the
 * Paid section and the job never waits for an invoice or gets auto-billed),
 * and tags the job noPayment. Money spent on the job (materials, labor, subs)
 * is not touched, so the job shows $0 collected and those costs as its loss.
 *
 * If the job already has an invoice: a $0 one is simply marked Paid; one with
 * an amount is left alone and an error says to handle it in Approved Invoices.
 * Returns { id, existed }.
 */
export const NO_PAYMENT_REASONS = ["Proposal", "Meeting", "Inspection", "Warranty", "Call-back", "No charge"];

export const markJobNoPayment = async (job, { reason = "No charge" } = {}) => {
  if (!job?.id) throw new Error("No job to mark.");
  const jobCollection = job.__collection === "addItem" ? "addItem" : "jobs";
  const now = new Date().toISOString();
  const why = String(reason || "No charge").trim();
  const paidFields = {
    status: "Paid",
    paidAt: now,
    paidAmount: 0,
    paymentMatchedFrom: "NoPaymentNeeded",
    noPayment: true,
    noPaymentReason: why,
    updatedAt: now,
  };
  const jobPatch = (invoiceId) => ({
    approvedInvoice: true,
    approvedInvoiceId: invoiceId,
    approvedInvoiceAt: now,
    billingKind: BILLING_KIND.STANDARD,
    billingDeferred: false,
    noPayment: true,
    noPaymentReason: why,
    noPaymentAt: now,
    updatedAt: serverTimestamp(),
  });

  const markExisting = async (snapDoc) => {
    const inv = snapDoc.data() || {};
    if (round2(parseMoney(inv.amount)) > 0 && inv.status !== "Paid") {
      throw new Error(
        `This job already has an invoice for ${formatMoney(inv.amount)} in Approved Invoices. ` +
          "Change or delete that invoice there first."
      );
    }
    if (inv.status !== "Paid") await updateDoc(snapDoc.ref, paidFields);
    await updateDoc(doc(db, jobCollection, job.id), jobPatch(snapDoc.id));
    return { id: snapDoc.id, existed: true };
  };

  const existingSnap = await getDocs(query(collection(db, "approvedInvoices"), where("sourceJobId", "==", job.id)));
  if (!existingSnap.empty) return markExisting(existingSnap.docs[0]);

  const invoiceData = stripUndefined({
    sourceJobId: job.id || "",
    jobId: job.id || "",
    wo: job.wo || "",
    siteName: job.siteName || "",
    propertyName: job.propertyName || "",
    companyName: job.companyName || "",
    management: job.management || "",
    managementCompany: job.management || job.managementCompany || "",
    address: job.address || "",
    city: job.city || "",
    state: job.state || "",
    zip: job.zip || "",
    cityStateZip: job.cityStateZip || "",
    scope: job.scope || "",
    proposalId: job.proposalId || "",
    invoiceDate: todayKey(),
    serviceDate: getCompletionDateKey(job) || dateKey(job.startDate) || "",
    terms: "No charge",
    productService: "Work Order",
    customProductService: "",
    description: `NO PAYMENT NEEDED - ${why}\n\n${buildStandardDescription(job)}`.trim(),
    billingHeader: "",
    qty: 1,
    rate: 0,
    amount: 0,
    billingKind: BILLING_KIND.STANDARD,
    billingOrder: 0,
    billingSequence: 0,
    contractTotal: 0,
    previouslyBilled: 0,
    remainingBalance: 0,
    createdFrom: "no-payment",
    createdAt: serverTimestamp(),
    ...paidFields,
  });

  const ref = doc(db, "approvedInvoices", `job_${job.id}`);
  const wrote = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return false;
    tx.set(ref, invoiceData);
    return true;
  });
  if (!wrote) return markExisting(await getDoc(ref));

  await updateDoc(doc(db, jobCollection, job.id), jobPatch(ref.id));
  return { id: ref.id, existed: false };
};

/**
 * "No Payment" on an invoice that is ALREADY in Approved Invoices: a proposal
 * visit, a meeting, an inspection that got an invoice but is not billed.
 *
 * The invoice becomes $0.00 and Paid (lands in the Paid section, tagged
 * "No payment · Meeting"), exactly like picking No Payment Needed in the
 * completion popup. What it said before (amount, rate, line rows, status) is
 * kept in noPaymentOriginal so Undo can put it back.
 *
 * Tech / sub pay is NOT touched: it comes from the job's techAssign, sub pay
 * lines and clock time, never from the invoice - so techs are still paid and
 * the job shows their pay as its cost.
 *
 * Refused when money was already recorded against the invoice, or when it is
 * a deposit / progress / final invoice (that would change a contract balance).
 * Returns the patch saved on the invoice.
 */
export const markInvoiceNoPayment = async (invoice = {}, { reason = "No charge", paidSoFar = 0 } = {}) => {
  if (!invoice?.id) throw new Error("No invoice to mark.");
  if (invoice.noPayment) throw new Error("This invoice is already marked No Payment.");
  if (round2(parseMoney(paidSoFar)) > 0) {
    throw new Error(
      `${formatMoney(paidSoFar)} was already received on this invoice, so it can't be No Payment. ` +
        "Remove that payment on Deposits first if it was recorded by mistake."
    );
  }
  if (invoice.billingKind && invoice.billingKind !== BILLING_KIND.STANDARD) {
    throw new Error(
      "This is a deposit / progress / final invoice on a contract. Changing it to No Payment would change the " +
        "contract balance - delete it or edit the amount instead."
    );
  }

  const why = String(reason || "No charge").trim();
  const now = new Date().toISOString();
  const description = String(invoice.description || "");
  const tagLine = `NO PAYMENT NEEDED - ${why}`;

  const patch = stripUndefined({
    noPaymentOriginal: {
      amount: invoice.amount ?? 0,
      rate: invoice.rate ?? 0,
      status: invoice.status || "Draft",
      terms: invoice.terms || "",
      description,
      serviceRows: Array.isArray(invoice.serviceRows) ? invoice.serviceRows : null,
    },
    amount: 0,
    rate: 0,
    // Line rows would still print their prices - zero them, keep the wording.
    ...(Array.isArray(invoice.serviceRows) && invoice.serviceRows.length
      ? { serviceRows: invoice.serviceRows.map((row) => ({ ...row, amount: 0, total: 0 })) }
      : {}),
    terms: "No charge",
    description: description.startsWith("NO PAYMENT NEEDED") ? description : `${tagLine}\n\n${description}`.trim(),
    status: "Paid",
    paidAt: now,
    paidAmount: 0,
    paymentMatchedFrom: "NoPaymentNeeded",
    noPayment: true,
    noPaymentReason: why,
    noPaymentAt: now,
    updatedAt: now,
  });

  await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);

  // Tag the job too (same marks the completion popup writes). Its techAssign,
  // sub pay and clock time stay exactly as they are.
  const jobId = invoice.sourceJobId || invoice.jobId;
  if (jobId) {
    try {
      await updateDoc(doc(db, "jobs", jobId), {
        noPayment: true,
        noPaymentReason: why,
        noPaymentAt: now,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Invoice marked No Payment; could not tag its job:", err);
    }
  }

  return patch;
};

/**
 * Undo "No Payment" on an invoice: puts back the amount, lines, terms,
 * wording and status it had (Draft when it was created as No Payment from
 * the start). Returns the patch saved.
 */
export const undoInvoiceNoPayment = async (invoice = {}) => {
  if (!invoice?.id || !invoice.noPayment) throw new Error("This invoice is not marked No Payment.");
  const orig = invoice.noPaymentOriginal || {};
  const now = new Date().toISOString();
  const origStatus = String(orig.status || "Draft");

  const patch = {
    amount: orig.amount ?? 0,
    rate: orig.rate ?? 0,
    terms: orig.terms || "Net 30",
    description:
      orig.description !== undefined
        ? orig.description
        : String(invoice.description || "").replace(/^NO PAYMENT NEEDED - [^\n]*\n*/, ""),
    status: origStatus === "Paid" ? "Draft" : origStatus,
    paidAt: null,
    paidAmount: 0,
    paymentMatchedFrom: null,
    noPayment: false,
    noPaymentReason: null,
    noPaymentAt: null,
    noPaymentOriginal: null,
    noPaymentUndoneAt: now,
    updatedAt: now,
  };
  if (Array.isArray(orig.serviceRows)) patch.serviceRows = orig.serviceRows;

  await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);

  const jobId = invoice.sourceJobId || invoice.jobId;
  if (jobId) {
    try {
      await updateDoc(doc(db, "jobs", jobId), {
        noPayment: false,
        noPaymentReason: null,
        noPaymentAt: null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("No Payment undone; could not update its job:", err);
    }
  }

  return patch;
};

/** The WO a job was continued to (5456 -> 5456-2), or "" if it never was. */
export const getContinuedToWO = (job = {}) =>
  String(job.continuedToWO || job.projectContinuedToWO || "").trim();

/**
 * "Bill Later - continues on WO 5456-2" (under No Payment Needed in the popup).
 *
 * The work is still going, so this WO is not billed by itself: its balance
 * rolls over to the continuation job, which carries the job total and is
 * billed when the work finishes. NO invoice is written (not even $0), so
 * nothing can be counted twice. The job is tagged billLater and drops off
 * "Completed jobs waiting for an invoice" and the automatic sweep.
 *
 * The continuation job is found by continuedFromJobId. If it has no job total
 * of its own, the original's total is copied onto it (only when empty), and a
 * contract the original was on is handed over - so the balance really does
 * roll over. Returns { toWO, toJobId }.
 */
export const markJobBillLater = async (job = {}) => {
  if (!job?.id) throw new Error("No job to mark.");
  if (job.__collection === "addItem") throw new Error("Only scheduled jobs can be billed on a continuation.");
  if (job.approvedInvoice) {
    throw new Error("This job already has an invoice in Approved Invoices. Change or delete that invoice there first.");
  }

  // The continuation job: Continue (Home / Job History / invoice pages) and
  // project days each leave their own link.
  let next = null;
  if (job.projectContinuedToJobId) {
    const snap = await getDoc(doc(db, "jobs", job.projectContinuedToJobId));
    if (snap.exists()) next = { id: snap.id, ...snap.data() };
  }
  if (!next) {
    const kids = await getDocs(query(collection(db, "jobs"), where("continuedFromJobId", "==", job.id)));
    if (!kids.empty) {
      // Newest continuation if there is more than one.
      const list = kids.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(b.startDate || "").localeCompare(String(a.startDate || "")));
      next = list[0];
    }
  }
  if (!next) {
    throw new Error(
      "Could not find the continuation job for this WO. Bill Later is only for jobs that continue on another WO."
    );
  }

  const toWO = String(next.wo || getContinuedToWO(job) || "");

  // Roll the balance over: the next WO must carry the total it will be billed on.
  const nextPatch = {};
  const nextTotal = await getJobTotalForBilling(next);
  if (!(nextTotal > 0)) {
    const myTotal = await getJobTotalForBilling(job);
    if (myTotal > 0) nextPatch.estimatedTotal = myTotal;
  }
  if (job.contractBilling?.contractId && !next.contractBilling?.contractId) {
    nextPatch.contractBilling = job.contractBilling;
  }
  if (Object.keys(nextPatch).length) {
    await updateDoc(doc(db, "jobs", next.id), {
      ...nextPatch,
      billLaterFromJobIds: arrayUnion(job.id),
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(doc(db, "jobs", next.id), { billLaterFromJobIds: arrayUnion(job.id) });
  }

  const now = new Date().toISOString();
  await updateDoc(doc(db, "jobs", job.id), {
    billLater: true,
    billLaterAt: now,
    billLaterToWO: toWO,
    billLaterToJobId: next.id,
    billingDeferred: false,
    updatedAt: serverTimestamp(),
  });

  return { toWO, toJobId: next.id, totalCopied: nextPatch.estimatedTotal || 0 };
};

/**
 * "Decide later" in the popup. The job stays in the waiting list on Approved
 * Invoices and the automatic sweep leaves it alone, because someone chose
 * not to bill it yet.
 */
export const deferJobBilling = async (job = {}) => {
  if (!job?.id || job.__collection === "addItem") return;
  try {
    await updateDoc(doc(db, "jobs", job.id), {
      billingDeferred: true,
      billingDeferredAt: new Date().toISOString(),
    });
  } catch (err) {
    console.warn("Could not mark billing as deferred:", err);
  }
};

/* ------------------------------------------------------------------------ */
/* 4. Keep open invoices' balance lines current                             */
/* ------------------------------------------------------------------------ */

/**
 * For a Draft / Reviewed ledger invoice, the header it SHOULD show given every
 * other invoice on the contract right now. Sent and Paid invoices are frozen:
 * a document already in the client's hands must not change under them.
 *
 * Returns the patch to save, or null when nothing changed.
 */
export const getRefreshedHeaderPatch = (invoice, allInvoices = []) => {
  if (!invoice?.billingKind || invoice.billingKind === BILLING_KIND.STANDARD) return null;
  if (!invoice.contractId) return null;

  const status = String(invoice.status || "Draft").toLowerCase();
  if (status === "sent" || status === "paid") return null;

  const others = allInvoices.filter(
    (inv) => inv.contractId === invoice.contractId && inv.id !== invoice.id && isLedgerInvoice(inv)
  );
  const myOrder = Number(invoice.billingOrder ?? 0);
  const myCreated = createdMillis(invoice);

  // Only invoices that came BEFORE this one count as previously billed.
  const before = others.filter((inv) => {
    const o = Number(inv.billingOrder ?? 0);
    if (o !== myOrder) return o < myOrder;
    return createdMillis(inv) < myCreated;
  });

  const previouslyBilled = round2(
    before.reduce((sum, inv) => sum + parseMoney(inv.amount ?? inv.rate), 0)
  );
  const progressBefore = before.filter((inv) => inv.billingKind === BILLING_KIND.PROGRESS).length;
  const amount = round2(parseMoney(invoice.amount ?? invoice.rate));
  const contractTotal = round2(parseMoney(invoice.contractTotal));

  const header = buildBillingHeader({
    scope: invoice.scope || invoice.siteName,
    kind: invoice.billingKind,
    sequence: invoice.billingKind === BILLING_KIND.PROGRESS ? progressBefore + 1 : 0,
    contractTotal,
    previouslyBilled: invoice.billingKind === BILLING_KIND.DEPOSIT ? 0 : previouslyBilled,
    amount,
  });

  if (!header || header === invoice.billingHeader) return null;

  const description = String(invoice.description || "");
  const oldHeader = String(invoice.billingHeader || "");

  // Only rewrite text we wrote. If someone edited the header lines by hand,
  // leave their wording alone and just keep the numbers on the record.
  let nextDescription = description;
  if (oldHeader && description.startsWith(oldHeader)) {
    nextDescription = header + description.slice(oldHeader.length);
  } else if (!description.trim()) {
    nextDescription = header;
  } else {
    return null;
  }

  return {
    description: nextDescription,
    billingHeader: header,
    previouslyBilled: invoice.billingKind === BILLING_KIND.DEPOSIT ? 0 : previouslyBilled,
    billingSequence:
      invoice.billingKind === BILLING_KIND.PROGRESS ? progressBefore + 1 : invoice.billingSequence || 0,
    remainingBalance: round2(
      contractTotal - (invoice.billingKind === BILLING_KIND.DEPOSIT ? 0 : previouslyBilled) - amount
    ),
  };
};

/* ------------------------------------------------------------------------ */
/* Go-live date for the automatic sweep                                      */
/* ------------------------------------------------------------------------ */

/**
 * The first day this flow ran. Jobs completed on or after it are invoiced
 * automatically; older completed jobs (the Send Invoices backlog) wait in a
 * list for a click, so turning this on does not mass-create old invoices.
 */
export const getAutoInvoiceSince = async () => {
  const ref = doc(db, ...SETTINGS_DOC);
  try {
    const snap = await getDoc(ref);
    const since = snap.exists() ? String(snap.data()?.autoInvoiceSince || "") : "";
    if (since) return since;

    const today = todayKey();
    await setDoc(ref, { autoInvoiceSince: today, createdAt: serverTimestamp() }, { merge: true });
    return today;
  } catch (err) {
    console.warn("Could not read billing settings; automatic invoicing paused:", err);
    return "9999-12-31";
  }
};
